import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { WorkSecurityRole } from "services/work-security.service";
import { groupCheckState, groupRolesByGroup, setGroupRolesChecked } from "./positions-helpers";

/**
 * One sphere's pareigybės checklist, grouped by `role.group` (positions edit
 * dialog, individual-add panel). A sphere with no groups at all renders the
 * same flat 2-column grid as before; once groups exist, each gets a small
 * header with a tri-state "select all" checkbox and an x/y counter, and the
 * ungrouped roles (if any) form their own bucket last.
 */
type Props = {
  roles: WorkSecurityRole[];
  checkedRoleIds: string[];
  onToggleRole: (roleId: string) => void;
  /** Replaces the full checked-id list — used for the group "select all" checkbox. */
  onSetCheckedRoleIds: (ids: string[]) => void;
};

export default function GroupedRoleCheckboxes({
  roles,
  checkedRoleIds,
  onToggleRole,
  onSetCheckedRoleIds,
}: Props) {
  const { t } = useTranslation();
  const buckets = groupRolesByGroup(roles);
  // "No groups at all" — the whole sphere is one ungrouped bucket: keep the
  // original flat list, no header.
  const flat = buckets.length === 0 || (buckets.length === 1 && buckets[0].groupId === "");

  if (flat) {
    return (
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {roles.map((role) => (
          <RoleCheckbox
            key={role.id}
            role={role}
            checked={checkedRoleIds.includes(role.id)}
            onChange={() => onToggleRole(role.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {buckets.map((bucket) => {
        const roleIds = bucket.roles.map((role) => role.id);
        const state = groupCheckState(roleIds, checkedRoleIds);
        const checkedCount = roleIds.filter((id) => checkedRoleIds.includes(id)).length;
        return (
          <div key={bucket.groupId || "__none__"}>
            <label className="mb-1 flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 max-md:py-1.5">
              <GroupTriStateCheckbox
                state={state}
                onChange={() =>
                  onSetCheckedRoleIds(setGroupRolesChecked(checkedRoleIds, roleIds, state !== "all"))
                }
              />
              <span>{bucket.groupId ? bucket.groupName : t("no_group")}</span>
              <span className="font-normal normal-case text-gray-400">
                ({checkedCount}/{roleIds.length})
              </span>
            </label>
            <div className="grid grid-cols-1 gap-1 pl-5 sm:grid-cols-2">
              {bucket.roles.map((role) => (
                <RoleCheckbox
                  key={role.id}
                  role={role}
                  checked={checkedRoleIds.includes(role.id)}
                  onChange={() => onToggleRole(role.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GroupTriStateCheckbox({ state, onChange }: { state: "none" | "some" | "all"; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === "all"}
      onChange={onChange}
      className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 max-md:h-5 max-md:w-5"
    />
  );
}

function RoleCheckbox({
  role,
  checked,
  onChange,
}: {
  role: WorkSecurityRole;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm max-md:py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 max-md:h-5 max-md:w-5"
      />
      <span className="text-gray-900">{role.name}</span>
    </label>
  );
}
